import { Button, Modal, Stack, Text, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InvoiceApi } from "../../apis";
import { message } from "../../utils/Message";
import { handleAmendError } from "./amend-error";
import { ModalFooter } from "../ui/app-modal";
import modalChrome from "../ui/app-modal.module.css";

type AmendmentRespondModalProps = {
  opened: boolean;
  onClose: () => void;
  invoiceId: string;
  amendmentId: string;
  onSuccess: () => void;
};

type FormValues = {
  rejectionReason: string;
};

export function AmendmentRespondModal({
  opened,
  onClose,
  invoiceId,
  amendmentId,
  onSuccess,
}: AmendmentRespondModalProps) {
  const { t } = useTranslation(["invoice", "common"]);
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: { rejectionReason: "" },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async () => {
    const reason = form.values.rejectionReason.trim();
    if (!reason) {
      message.error(t("amend.rejectReasonRequired"));
      return;
    }

    try {
      setLoading(true);
      await InvoiceApi.respondAmendment(invoiceId, amendmentId, {
        accept: false,
        rejectionReason: reason,
      });
      message.success(t("amend.rejectedToast"));
      onSuccess();
      handleClose();
    } catch (error) {
      handleAmendError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("amend.reject")}
      size="md"
      classNames={{ body: modalChrome.modalBodyFlush }}
    >
      <Stack gap="md" px={24} pb={20}>
        <Text size="sm" c="dimmed">
          {t("amend.rejectDescription")}
        </Text>
        <Textarea
          label={t("amend.rejectReasonLabel")}
          placeholder={t("amend.rejectReasonPlaceholder")}
          {...form.getInputProps("rejectionReason")}
          minRows={3}
          maxLength={500}
          required
        />
      </Stack>
      <ModalFooter>
        <Button variant="default" onClick={handleClose}>
          {t("common:cancel")}
        </Button>
        <Button color="red" loading={loading} onClick={handleSubmit}>
          {t("amend.reject")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
